import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Check, X, ArrowRight, Loader2, Tag, CheckCircle, Mail, Zap, Crown, Sparkles } from 'lucide-react';
import { formatPrice } from '../../config/pricing';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TIER_ICONS = {
  starter: Zap,
  core: Sparkles,
  pro: Crown,
};

const TIER_FEATURES_DISPLAY = [
  { label: 'AI reflections / month', key: 'interactions_per_month', format: (v) => v === -1 ? 'Unlimited' : v },
  { label: 'People tracked', key: 'active_people', format: (v) => v === -1 ? 'Unlimited' : v },
  { label: 'Projects tracked', key: 'active_projects', format: (v) => v === -1 ? 'Unlimited' : v },
  { label: 'Achievements', key: 'achievements', format: (v) => v === -1 ? 'Unlimited' : v },
  { label: 'Memory window', key: 'memory_window_days', format: (v) => v >= 365 ? `${Math.round(v/365)} year${v >= 730 ? 's' : ''}` : `${v} days` },
  { label: 'Pattern analysis', key: 'pattern_analysis', format: (v) => v },
  { label: 'Cross-month insights', key: 'cross_month_analysis', format: (v) => v },
  { label: 'Behavioural change detection', key: 'behavioural_change_detection', format: (v) => v },
  { label: 'Structured summaries', key: 'features.can_generate_structured_summary', format: (v) => v },
  { label: 'Full narrative export', key: 'features.can_generate_full_narrative', format: (v) => v },
  { label: 'Living CV sync', key: 'features.can_sync_living_cv', format: (v) => v },
  { label: 'Gap analysis', key: 'features.can_run_gap_analysis', format: (v) => v },
];

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

// Billing Toggle - supports weekly, monthly, annual
const BillingToggle = ({ value, onChange, savingsText, showWeekly = false }) => (
  <div className="flex items-center bg-zinc-800 rounded-full p-1 gap-1">
    {showWeekly && (
      <button
        onClick={() => onChange('weekly')}
        data-testid="billing-toggle-weekly"
        className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
          value === 'weekly' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
        }`}
      >
        Weekly
        {value !== 'weekly' && <span className="text-xs text-emerald-500 font-semibold">5-day trial</span>}
      </button>
    )}
    <button
      onClick={() => onChange('monthly')}
      data-testid="billing-toggle-monthly"
      className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
        value === 'monthly' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
      }`}
    >
      Monthly
    </button>
    <button
      onClick={() => onChange('annual')}
      data-testid="billing-toggle-annual"
      className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
        value === 'annual' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
      }`}
    >
      Annual
      {savingsText && <span className="text-xs text-emerald-500 font-semibold">{savingsText}</span>}
    </button>
  </div>
);

// Single Tier Card - updated to support weekly billing
const TierCard = ({ tier, billingInterval, onSubscribe, isLoading, isRecommended }) => {
  const Icon = TIER_ICONS[tier.id] || Zap;
  const isFree = tier.price_monthly === 0;
  const isComingSoon = tier.coming_soon;
  
  // Determine price based on billing interval
  let price, period, periodLabel;
  if (billingInterval === 'weekly' && tier.price_weekly) {
    price = tier.price_weekly;
    period = '/week';
    periodLabel = 'after 5-day free trial';
  } else if (billingInterval === 'annual') {
    price = tier.price_annual;
    period = '/year';
    periodLabel = null;
  } else {
    price = tier.price_monthly;
    period = '/month';
    periodLabel = null;
  }
  
  const monthlyEquivalent = billingInterval === 'annual' && tier.price_annual ? tier.price_annual / 12 : null;
  const hasWeeklyTrial = billingInterval === 'weekly' && tier.price_weekly && (tier.trial_days_weekly > 0 || tier.trial_days > 0);

  return (
    <div
      className={`relative rounded-2xl border transition-all duration-300 flex flex-col ${
        isRecommended 
          ? 'border-blue-500 bg-zinc-900 shadow-lg shadow-blue-500/10 scale-[1.02]' 
          : 'border-zinc-800 bg-zinc-900/60 hover:border-zinc-700'
      }`}
      data-testid={`tier-card-${tier.id}`}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-blue-600 text-white border-0 px-4 py-1 text-xs font-semibold">
            Recommended
          </Badge>
        </div>
      )}

      {tier.badge && !isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-zinc-700 text-zinc-300 border-0 px-3 py-1 text-xs">
            {tier.badge}
          </Badge>
        </div>
      )}

      <div className="p-6 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-5 h-5" style={{ color: tier.color }} />
          <h3 className="text-lg font-semibold text-white">{tier.name}</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-5">{tier.tagline}</p>

        {/* Price */}
        <div className="mb-5">
          {isFree ? (
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-white">Free</span>
            </div>
          ) : isComingSoon ? (
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-zinc-500">Contact us</span>
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-white">{formatPrice(price)}</span>
                <span className="text-zinc-500 text-sm">{period}</span>
              </div>
              {hasWeeklyTrial && (
                <p className="text-xs text-emerald-500 font-medium mt-1">
                  {periodLabel}
                </p>
              )}
              {billingInterval === 'annual' && monthlyEquivalent && (
                <p className="text-xs text-zinc-500 mt-1">
                  {formatPrice(monthlyEquivalent)}/month, billed annually
                </p>
              )}
              {billingInterval === 'annual' && tier.annual_savings?.savings > 0 && (
                <p className="text-xs text-emerald-500 font-medium mt-0.5">
                  Save {formatPrice(tier.annual_savings.savings)} ({tier.annual_savings.months_free} months free)
                </p>
              )}
              {billingInterval === 'weekly' && tier.price_weekly && (
                <p className="text-xs text-zinc-500 mt-1">
                  ~{formatPrice(tier.price_weekly * 4.33)}/month equivalent
                </p>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        <div className="mb-6">
          {isFree ? (
            <Link to="/login?from=free-tier" className="block">
              <Button
                variant="outline"
                className="w-full border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white"
                data-testid="tier-cta-starter"
              >
                Get Started Free
              </Button>
            </Link>
          ) : isComingSoon ? (
            <Button
              variant="outline"
              className="w-full border-zinc-700 text-zinc-500 cursor-not-allowed"
              disabled
              data-testid={`tier-cta-${tier.id}`}
            >
              Coming Soon
            </Button>
          ) : (
            <Button
              className={`w-full group ${isRecommended ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'}`}
              onClick={() => onSubscribe(tier.id, billingInterval)}
              disabled={isLoading}
              data-testid={`tier-cta-${tier.id}`}
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : hasWeeklyTrial ? (
                <>Start Free Trial<ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></>
              ) : (
                <>Start {tier.name}<ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></>
              )}
            </Button>
          )}
        </div>

        {/* Features */}
        <div className="space-y-2.5 flex-1">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Includes</p>
          {TIER_FEATURES_DISPLAY.map((feature) => {
            const value = getNestedValue(tier, feature.key);
            if (value === undefined || value === null) return null;
            const formatted = feature.format(value);
            const isBool = typeof formatted === 'boolean';
            const isActive = isBool ? formatted : true;

            return (
              <div key={feature.key} className="flex items-center gap-2.5 text-sm">
                {isActive ? (
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                ) : (
                  <X className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                )}
                <span className={isActive ? 'text-zinc-300' : 'text-zinc-600'}>
                  {isBool ? feature.label : `${formatted} ${feature.label.toLowerCase()}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SeriesPricing = ({ series }) => {
  const [tiers, setTiers] = useState([]);
  const [billingFrequency, setBillingFrequency] = useState('monthly');
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [validatedDiscount, setValidatedDiscount] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [pendingCheckout, setPendingCheckout] = useState(null);

  useEffect(() => {
    const loadTiers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/entitlements/tiers`);
        if (response.ok) {
          const data = await response.json();
          // Filter out leadership_plus for now (coming soon) — keep starter, core, pro
          setTiers(data.tiers.filter(t => ['starter', 'core', 'pro'].includes(t.id)));
        }
      } catch (err) {
        console.error('Failed to load tiers:', err);
      }
    };
    loadTiers();
  }, []);

  const validateDiscountCode = async (productId) => {
    if (!discountCode.trim()) {
      setDiscountError(null);
      setValidatedDiscount(null);
      return null;
    }
    setValidatingCode(true);
    setDiscountError(null);
    try {
      const body = { code: discountCode.trim() };
      if (productId) body.product_id = productId;
      const response = await fetch(`${API_URL}/api/payments/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.valid) {
        setValidatedDiscount({ ...data, forProduct: productId || null });
        setDiscountError(null);
        return data;
      } else {
        setDiscountError(data.message || 'Invalid code');
        setValidatedDiscount(null);
        return null;
      }
    } catch (err) {
      setDiscountError('Failed to validate code');
      setValidatedDiscount(null);
      return null;
    } finally {
      setValidatingCode(false);
    }
  };

  const executeCheckout = async (productId, email, extraFields = {}) => {
    setError(null);
    try {
      const body = {
        product_id: productId,
        origin_url: window.location.origin,
        discount_code: discountCode.trim() || undefined,
        ...extraFields
      };
      if (email) body.customer_email = email;
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.detail || 'Failed to create checkout session');
        setLoadingTier(null);
      }
    } catch (err) {
      setError('Unable to connect to payment service. Please try again.');
      setLoadingTier(null);
    }
  };

  const handleEmailSubmit = () => {
    const email = customerEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setShowEmailModal(false);
    if (pendingCheckout) {
      const { productId, extraFields } = pendingCheckout;
      setLoadingTier(productId);
      executeCheckout(productId, email, extraFields);
      setPendingCheckout(null);
    }
  };

  const handleSubscribe = (tierId, billing) => {
    // Map billing interval to product ID
    let productId;
    if (billing === 'weekly') {
      productId = `${tierId}_weekly`;
    } else if (billing === 'annual') {
      productId = `${tierId}_annual`;
    } else {
      productId = `${tierId}_monthly`;
    }
    setLoadingTier(tierId);
    executeCheckout(productId, null, { billing_frequency: billing });
  };

  const accentColor = series?.accentColor || '#3B82F6';

  return (
    <section id="pricing" className="py-24 bg-zinc-950 relative overflow-hidden">
      <div
        className="absolute top-0 left-0 w-full h-px"
        style={{ background: `linear-gradient(to right, transparent, ${accentColor}40, transparent)` }}
      />

      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 border border-zinc-700 bg-zinc-800/50 text-zinc-300">
            Simple, Transparent Pricing
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            Choose Your <span style={{ color: accentColor }}>Level</span>
          </h2>
          <p className="text-base text-zinc-400 max-w-2xl mx-auto mb-8">
            Start free. Upgrade when you need deeper insight, longer memory, and career-grade exports.
          </p>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-2">
            <BillingToggle
              value={billingFrequency}
              onChange={setBillingFrequency}
              savingsText="Save 17%"
              showWeekly={true}
            />
          </div>
        </div>

        {error && (
          <div className="max-w-3xl mx-auto mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        {/* Tier Cards */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6 mb-16">
          {tiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              billingInterval={billingFrequency}
              onSubscribe={handleSubscribe}
              isLoading={loadingTier === tier.id}
              isRecommended={tier.recommended}
            />
          ))}
        </div>

        {/* Books Section — warm, light background matching cover aesthetics */}
        <div className="relative -mx-6 lg:-mx-12 px-6 lg:px-12 py-16 mb-16"
             style={{ background: 'linear-gradient(to bottom, #faf8f5, #f5f0ea)' }}>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-zinc-900 mb-2">The Books — All 5 Available Now</h3>
              <p className="text-sm text-zinc-600">Stand-alone digital books &middot; start with Book 1 or dip into any title &middot; yours to keep forever</p>
              <p className="text-xs text-zinc-500 mt-2 lg:hidden">Swipe to browse &rarr;</p>
            </div>
            <div
              className="flex overflow-x-auto snap-x snap-mandatory gap-5 pb-6 -mx-6 lg:-mx-12 px-6 lg:px-12 scroll-smooth"
              style={{ scrollbarWidth: 'thin' }}
              data-testid="books-carousel"
            >
              {/* Book 1 — Active (Direct + Amazon secondary) */}
              <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col flex-shrink-0 w-[300px] snap-start" data-testid="book1-card">
                <div className="flex items-center gap-4 p-5">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1149db47-69fd-49d0-a436-8d432b8e84b4/artifacts/2thw5zsx_30DS%20-%20B01%20-%20print%20-%20front.jpg"
                    alt="First Steps book cover"
                    className="w-24 h-auto rounded-lg shadow-md flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-rose-100 text-rose-700 border-0 text-xs mb-2">Book 1 &middot; Start here</Badge>
                    <h4 className="text-lg font-bold text-zinc-900 leading-tight mb-1">First Steps</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">Foundations of Effective IT Leadership. 30 daily lessons to build your leadership foundations.</p>
                  </div>
                </div>
                <div className="px-5 pb-5 mt-auto">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-zinc-900">£6.99</span>
                    <span className="text-xs text-zinc-500">one-time &middot; PDF + ePub</span>
                  </div>
                  <Button
                    className="w-full bg-rose-500 hover:bg-rose-600 text-white font-semibold"
                    onClick={() => {
                      setLoadingTier('book1');
                      executeCheckout('book1', null);
                    }}
                    disabled={loadingTier === 'book1'}
                    data-testid="buy-book1-btn"
                  >
                    {loadingTier === 'book1' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      'Buy Book 1'
                    )}
                  </Button>
                  <a
                    href="https://www.amazon.co.uk/dp/B0GKPPK9M8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 block text-center text-xs text-zinc-500 hover:text-zinc-800 underline"
                    data-testid="book1-amazon-link"
                  >
                    Also on Amazon Kindle &rarr;
                  </a>
                </div>
              </div>

              {/* Book 2 — Active */}
              <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col flex-shrink-0 w-[300px] snap-start" data-testid="book2-card">
                <div className="flex items-center gap-4 p-5">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1149db47-69fd-49d0-a436-8d432b8e84b4/artifacts/1i7mr4sw_30DS%20-%20B02.jpg"
                    alt="Stepping Up book cover"
                    className="w-24 h-auto rounded-lg shadow-md flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-orange-100 text-orange-700 border-0 text-xs mb-2">Book 2</Badge>
                    <h4 className="text-lg font-bold text-zinc-900 leading-tight mb-1">Stepping Up</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">Turning Peer Relationships into Leadership Success. 30 daily lessons for new managers.</p>
                  </div>
                </div>
                <div className="px-5 pb-5 mt-auto">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-zinc-900">£6.99</span>
                    <span className="text-xs text-zinc-500">one-time &middot; PDF + ePub</span>
                  </div>
                  <Button
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                    onClick={() => {
                      setLoadingTier('book2');
                      executeCheckout('book2', null);
                    }}
                    disabled={loadingTier === 'book2'}
                    data-testid="buy-book2-btn"
                  >
                    {loadingTier === 'book2' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      'Buy Book 2'
                    )}
                  </Button>
                </div>
              </div>

              {/* Book 3 — Active */}
              <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col flex-shrink-0 w-[300px] snap-start" data-testid="book3-card">
                <div className="flex items-center gap-4 p-5">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1149db47-69fd-49d0-a436-8d432b8e84b4/artifacts/wmiot56p_30DS%20-%20B03.jpg"
                    alt="Clarity in Chaos book cover"
                    className="w-24 h-auto rounded-lg shadow-md flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-yellow-100 text-yellow-800 border-0 text-xs mb-2">Book 3</Badge>
                    <h4 className="text-lg font-bold text-zinc-900 leading-tight mb-1">Clarity in Chaos</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">Mastering Organisation for IT Leaders. 30 daily lessons to build systems that work.</p>
                  </div>
                </div>
                <div className="px-5 pb-5 mt-auto">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-zinc-900">£6.99</span>
                    <span className="text-xs text-zinc-500">one-time &middot; PDF + ePub</span>
                  </div>
                  <Button
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold"
                    onClick={() => {
                      setLoadingTier('book3');
                      executeCheckout('book3', null);
                    }}
                    disabled={loadingTier === 'book3'}
                    data-testid="buy-book3-btn"
                  >
                    {loadingTier === 'book3' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      'Buy Book 3'
                    )}
                  </Button>
                </div>
              </div>

              {/* Book 4 — Active */}
              <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col flex-shrink-0 w-[300px] snap-start" data-testid="book4-card">
                <div className="flex items-center gap-4 p-5">
                  <img
                    src="https://customer-assets.emergentagent.com/job_41f2b231-df69-4121-9f07-63f0ed4adaf8/artifacts/81bfa9nm_30DS%20-%20B04%20-%20print%20-%20front.jpg"
                    alt="Leading with Integrity book cover"
                    className="w-24 h-auto rounded-lg shadow-md flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-xs mb-2">Book 4</Badge>
                    <h4 className="text-lg font-bold text-zinc-900 leading-tight mb-1">Leading with Integrity</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">Ethical Practices for IT Managers. 30 daily lessons in transparent, accountable leadership.</p>
                  </div>
                </div>
                <div className="px-5 pb-5 mt-auto">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-zinc-900">£6.99</span>
                    <span className="text-xs text-zinc-500">one-time &middot; PDF + ePub</span>
                  </div>
                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                    onClick={() => {
                      setLoadingTier('book4');
                      executeCheckout('book4', null);
                    }}
                    disabled={loadingTier === 'book4'}
                    data-testid="buy-book4-btn"
                  >
                    {loadingTier === 'book4' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      'Buy Book 4'
                    )}
                  </Button>
                </div>
              </div>

              {/* Book 5 — Active (NEWEST) */}
              <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col flex-shrink-0 w-[300px] snap-start ring-2 ring-emerald-500" data-testid="book5-card">
                <div className="flex items-center gap-4 p-5">
                  <img
                    src="https://customer-assets.emergentagent.com/job_41f2b231-df69-4121-9f07-63f0ed4adaf8/artifacts/wr9ljc82_30DS%20-%20B05%20-%20print%20-%20front.jpg"
                    alt="Communicating for Impact book cover"
                    className="w-24 h-auto rounded-lg shadow-md flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs mb-2">NEW &middot; Book 5</Badge>
                    <h4 className="text-lg font-bold text-zinc-900 leading-tight mb-1">Communicating for Impact</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">The IT Manager&apos;s Guide to Influence. 30 daily lessons to be heard, understood, and followed.</p>
                  </div>
                </div>
                <div className="px-5 pb-5 mt-auto">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-zinc-900">£6.99</span>
                    <span className="text-xs text-zinc-500">one-time &middot; PDF + ePub</span>
                  </div>
                  <Button
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                    onClick={() => {
                      setLoadingTier('book5');
                      executeCheckout('book5', null);
                    }}
                    disabled={loadingTier === 'book5'}
                    data-testid="buy-book5-btn"
                  >
                    {loadingTier === 'book5' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      'Buy Book 5'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Discount Code */}
        <div className="max-w-md mx-auto mb-12" id="discount-code-section">
          <div className="flex items-center gap-2 p-1 bg-zinc-900 rounded-lg border border-zinc-800">
            <div className="flex items-center pl-3 text-zinc-500">
              <Tag className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value.toUpperCase());
                setValidatedDiscount(null);
                setDiscountError(null);
              }}
              placeholder="Have a discount code?"
              className="flex-1 px-2 py-2 text-sm bg-transparent border-none focus:outline-none text-zinc-200 placeholder:text-zinc-600"
              data-testid="discount-code-input"
            />
            {discountCode && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => validateDiscountCode(null)}
                disabled={validatingCode}
                className="text-xs text-zinc-400 hover:text-white"
              >
                {validatingCode ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
              </Button>
            )}
          </div>
          {validatedDiscount && (
            <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">{validatedDiscount.code}</span>
                <span>—</span>
                <span>{validatedDiscount.message}</span>
              </div>
            </div>
          )}
          {discountError && (
            <p className="mt-2 text-red-400 text-sm text-center">{discountError}</p>
          )}
        </div>

        {/* Trust elements */}
        <div className="flex flex-wrap justify-center gap-8 text-sm text-zinc-500 mb-8">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            <span>Secure Stripe payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            <span>Free tier, no card required</span>
          </div>
        </div>

        {/* Legal */}
        <div className="text-center">
          <p className="text-xs text-zinc-600">
            By subscribing, you agree to our{' '}
            <Link to="/terms-of-sale" className="underline hover:text-zinc-400">Terms of Sale</Link>
            {' '}and{' '}
            <Link to="/privacy" className="underline hover:text-zinc-400">Privacy Policy</Link>.
            {' '}See{' '}
            <Link to="/companion-terms" className="underline hover:text-zinc-400">Companion Terms</Link>.
          </p>
        </div>
      </div>

      {/* Email Collection Modal for Free Orders */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-md w-full shadow-2xl relative">
            <button
              onClick={() => { setShowEmailModal(false); setLoadingTier(null); setPendingCheckout(null); }}
              className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300"
              data-testid="email-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-500/10">
                <Mail className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Almost there!</h3>
              <p className="text-sm text-zinc-400 mb-6">
                Enter your email to activate your access.
              </p>
              <div className="space-y-3">
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => { setCustomerEmail(e.target.value); setEmailError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-zinc-500"
                  autoFocus
                  data-testid="free-order-email-input"
                />
                {emailError && <p className="text-red-400 text-xs text-left">{emailError}</p>}
                <Button
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  onClick={handleEmailSubmit}
                  data-testid="free-order-email-submit"
                >
                  Activate Access
                </Button>
              </div>
              <p className="text-xs text-zinc-600 mt-4">We respect your privacy. No spam, ever.</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SeriesPricing;
